/**
 * React state + orchestration for the WagePro migration-import wizard
 * (`employee_migration_screen.tsx`). Extracted out of the screen so the screen
 * holds only JSX (CLAUDE.md: components >150 lines keep JSX + wiring, logic in
 * co-located modules). Every PURE transform this hook calls lives in
 * `employee_migration_logic.ts` / `migration_upload_logic.ts` /
 * `migration_mapping_logic.ts`; this hook is the React wiring that connects them
 * to the data hook (`use_wagepro_migration`).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { MigrationJurisdiction } from '@jumpstone/core';
import { navigate } from '../../../lib/router';
import {
  useWageproMigration,
  type MigrationFailure,
  type MigrationReport,
  type MigrationRunReport,
  type MigrationTemplateInfo,
} from '../../../data/use_wagepro_migration';
import {
  canLeaveStart,
  classifyCommitProgress,
  initialWizardState,
  resumeStepForStatus,
  type MigrationScope,
  type MigrationStepId,
  type WizardState,
} from './employee_migration_logic';
import { buildStageSheets, type DetectedSheet } from './migration_upload_logic';
import {
  defaultMapping,
  headersAlreadyMatch,
  type SheetMappings,
} from './migration_mapping_logic';

/** A single commit poll's budget — a runaway backstop, not the real exit (F8). */
const MAX_COMMIT_POLLS = 1000;

export type CommitStatus = 'idle' | 'running' | 'stalled' | 'failed';

export interface MigrationWizard {
  entityStatus: 'ready' | 'no_entity';
  canWrite: boolean;
  canUndo: boolean;
  wizard: WizardState;
  /** ISO date the cut-over gate treats as "today"; the latest pickable date. */
  today: string;
  templateInfo: MigrationTemplateInfo | null;
  detectedSheets: DetectedSheet[];
  mappings: SheetMappings;
  report: MigrationReport | null;
  runReport: MigrationRunReport | null;
  jurisdiction: MigrationJurisdiction;
  warningCount: number;
  acknowledge: boolean;
  staging: boolean;
  committing: boolean;
  commitStatus: CommitStatus;
  undoing: boolean;
  undoError: MigrationFailure | null;
  canContinueStart: boolean;
  canImport: boolean;
  // Actions
  setScope: (scope: MigrationScope) => void;
  setCutover: (value: string) => void;
  setDetectedSheets: (sheets: DetectedSheet[]) => void;
  setAcknowledge: (value: boolean) => void;
  goTo: (step: MigrationStepId) => void;
  proceedFromUpload: () => void;
  proceedFromMap: (next: SheetMappings) => void;
  runCommit: (runId: string) => void;
  runUndo: () => void;
}

export function useMigrationWizard(
  initialScope?: MigrationScope,
): MigrationWizard {
  const migration = useWageproMigration();
  const today = new Date().toISOString().slice(0, 10);

  const [wizard, setWizard] = useState(() =>
    initialWizardState({ scope: initialScope }),
  );
  const [templateInfo, setTemplateInfo] =
    useState<MigrationTemplateInfo | null>(null);
  const [detectedSheets, setDetectedSheets] = useState<DetectedSheet[]>([]);
  const [mappings, setMappings] = useState<SheetMappings>({});
  const [report, setReport] = useState<MigrationReport | null>(null);
  const [runReport, setRunReport] = useState<MigrationRunReport | null>(null);
  const [acknowledge, setAcknowledge] = useState(false);
  const [staging, setStaging] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [commitStatus, setCommitStatus] = useState<CommitStatus>('idle');
  const [undoing, setUndoing] = useState(false);
  const [undoError, setUndoError] = useState<MigrationFailure | null>(null);
  const resumedRef = useRef(false);

  const jurisdiction: MigrationJurisdiction =
    templateInfo?.jurisdiction ?? 'LK';
  const warningCount =
    report?.issues.filter((i) => i.severity === 'warning').length ?? 0;

  // Load the entity template context + resume any in-progress run on mount.
  useEffect(() => {
    if (resumedRef.current) return;
    if (!migration.template || !migration.listRuns) return;
    resumedRef.current = true;
    void (async () => {
      const tpl = await migration.template?.();
      if (tpl?.outcome === 'success') setTemplateInfo(tpl.value);
      const runs = await migration.listRuns?.();
      if (runs?.outcome !== 'success') return;
      // Resume ONLY an in-progress run; a committed/aborted/failed run is
      // finished — start fresh rather than dead-end on its Done screen (F4).
      const active = runs.value.find(
        (r) => r.status === 'loading' || r.status === 'dry_run_ready',
      );
      if (active && migration.getReport) {
        const rep = await migration.getReport(active.id);
        if (rep.outcome === 'success') {
          setRunReport(rep.value);
          setWizard((w) => ({
            ...w,
            run_id: active.id,
            cutover_date: rep.value.cutover_date ?? w.cutover_date,
            step: resumeStepForStatus(active.status),
          }));
        }
      }
    })();
  }, [migration]);

  const goTo = useCallback((step: MigrationStepId) => {
    setWizard((w) => ({ ...w, step }));
  }, []);

  const runStage = useCallback(async () => {
    if (!migration.stage) return;
    const sheets = buildStageSheets(detectedSheets, mappings);
    if (!sheets) return;
    setStaging(true);
    try {
      const res = await migration.stage({
        run_id: wizard.run_id ?? undefined,
        cutover_date: wizard.cutover_date,
        sheets,
      });
      if (res.outcome === 'success') {
        setReport(res.value);
        setWizard((w) => ({ ...w, run_id: res.value.run_id }));
      }
    } finally {
      setStaging(false);
    }
  }, [migration, detectedSheets, mappings, wizard.run_id, wizard.cutover_date]);

  const runCommit = useCallback(
    (runId: string) => {
      if (!migration.commit) return;
      const commit = migration.commit;
      setCommitting(true);
      setCommitStatus('running');
      void (async () => {
        let prevLoaded = -1;
        try {
          for (let i = 0; i < MAX_COMMIT_POLLS; i += 1) {
            const res = await commit(runId, acknowledge);
            if (res.outcome !== 'success') {
              setCommitStatus('stalled'); // transient — Continue can retry
              break;
            }
            setRunReport((prev) => ({
              run_id: runId,
              status: res.value.status,
              cutover_date: prev?.cutover_date ?? wizard.cutover_date,
              stats: prev?.stats ?? null,
              item_counts: prev?.item_counts ?? {},
              loaded: res.value.loaded,
              total: res.value.total,
            }));
            const action = classifyCommitProgress(
              res.value.status,
              res.value.loaded,
              prevLoaded,
            );
            prevLoaded = res.value.loaded;
            if (action === 'done') {
              goTo('done');
              break;
            }
            if (action === 'failed') {
              setCommitStatus('failed');
              break;
            }
            if (action === 'stalled') {
              setCommitStatus('stalled');
              break;
            }
          }
        } finally {
          setCommitting(false);
        }
      })();
    },
    [migration, acknowledge, wizard.cutover_date, goTo],
  );

  const runUndo = useCallback(() => {
    if (!migration.undo || !wizard.run_id) return;
    const undo = migration.undo;
    const runId = wizard.run_id;
    setUndoing(true);
    setUndoError(null);
    void (async () => {
      try {
        const res = await undo(runId);
        if (res.outcome === 'success') {
          navigate({ module: 'employees', view: 'directory' });
        } else {
          // Undo can be legitimately refused — surface it, never navigate away
          // as if it succeeded (F3).
          setUndoError(res.failure);
        }
      } finally {
        setUndoing(false);
      }
    })();
  }, [migration, wizard.run_id]);

  const proceedFromUpload = useCallback(() => {
    const needsMapping = detectedSheets.some(
      (s) =>
        s.kind !== null &&
        !headersAlreadyMatch(s.headers, s.kind, jurisdiction),
    );
    if (needsMapping) {
      const initial: SheetMappings = {};
      for (const s of detectedSheets) {
        if (s.kind) {
          initial[s.sourceName] = defaultMapping(
            s.headers,
            s.kind,
            jurisdiction,
          );
        }
      }
      setMappings(initial);
      goTo('map');
    } else {
      goTo('review');
      void runStage();
    }
  }, [detectedSheets, jurisdiction, goTo, runStage]);

  const proceedFromMap = useCallback(
    (next: SheetMappings) => {
      setMappings(next);
      goTo('review');
      void runStage();
    },
    [goTo, runStage],
  );

  const setScope = useCallback((scope: MigrationScope) => {
    setWizard((w) => ({ ...w, scope }));
  }, []);
  const setCutover = useCallback((cutover_date: string) => {
    setWizard((w) => ({ ...w, cutover_date }));
  }, []);

  const canImport =
    report !== null && !report.blocking && (warningCount === 0 || acknowledge);

  return {
    entityStatus: migration.entity_status,
    canWrite: migration.can_write,
    canUndo: Boolean(migration.undo),
    wizard,
    templateInfo,
    detectedSheets,
    mappings,
    report,
    runReport,
    jurisdiction,
    warningCount,
    acknowledge,
    staging,
    committing,
    commitStatus,
    undoing,
    undoError,
    today,
    canContinueStart: canLeaveStart(wizard, today),
    canImport,
    setScope,
    setCutover,
    setDetectedSheets,
    setAcknowledge,
    goTo,
    proceedFromUpload,
    proceedFromMap,
    runCommit,
    runUndo,
  };
}
